import { useEffect, useState } from 'react';
import { useCanonical } from '@/hooks/useCanonical';
import { Sparkles, Calendar, Zap, Info, Baby, Shield, Heart, ChevronRight, CheckCircle2, AlertTriangle, Clock, MapPin, Phone, CreditCard, Users, Droplets, Sun, Scissors, Stethoscope } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { motion, AnimatePresence } from 'framer-motion';
import CTASection from '@/components/CTASection';

interface FAQCategory {
  id: string;
  label: string;
  icon: React.ReactNode;
  emoji: string;
  items: { question: string; answer: React.ReactNode }[];
}

const faqCategories: FAQCategory[] = [
  {
    id: 'nos-soins',
    label: 'Nos soins',
    icon: <Sparkles size={18} />,
    emoji: '✨',
    items: [
      {
        question: 'Quels types de soins visage proposez-vous à Sevran ?',
        answer: (
          <div className="space-y-4">
            <p>Chez Body by CC, nous proposons une <strong>gamme complète de soins visage</strong> à Sevran (93270), adaptés à chaque type de peau :</p>
            <ul className="space-y-2.5">
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>Hydratation & éclat</strong> — Retrouvez un teint lumineux et une peau repulpée</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>Anti-âge & raffermissement</strong> — Lissez les rides et retendez la peau</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>Peeling chimique</strong> — Renouvelez votre grain de peau en douceur</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>Photothérapie LED</strong> — Rouge (anti-âge), bleue (anti-acné)</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>Skincleantech</strong> — Nettoyage de peau profond 3-en-1</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>HIFU lifting</strong> — Alternative non chirurgicale au lifting</span></li>
            </ul>
            <p className="text-muted-foreground/80 text-sm italic">Chaque soin est précédé d'un diagnostic de peau personnalisé. Nous accueillons les clientes de Sevran, Aulnay-sous-Bois, Villepinte, Tremblay-en-France et toute la Seine-Saint-Denis.</p>
          </div>
        ),
      },
      {
        question: "Qu'est-ce que le HIFU et quels résultats puis-je espérer ?",
        answer: (
          <div className="space-y-4">
            <p>Le <strong>HIFU</strong> (High Intensity Focused Ultrasound) utilise des <strong>ultrasons focalisés</strong> pour stimuler la production de collagène en profondeur — <em>sans chirurgie ni injection</em>.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-4">
              <div className="rounded-xl bg-primary/5 border border-primary/10 p-4 text-center">
                <p className="font-heading font-bold text-primary text-lg">2-3 mois</p>
                <p className="text-xs text-muted-foreground mt-1">Résultats progressifs</p>
              </div>
              <div className="rounded-xl bg-primary/5 border border-primary/10 p-4 text-center">
                <p className="font-heading font-bold text-primary text-lg">12-18 mois</p>
                <p className="text-xs text-muted-foreground mt-1">Durée des effets</p>
              </div>
              <div className="rounded-xl bg-primary/5 border border-primary/10 p-4 text-center">
                <p className="font-heading font-bold text-primary text-lg">1-2 /an</p>
                <p className="text-xs text-muted-foreground mt-1">Séances d'entretien</p>
              </div>
            </div>
            <p><strong>Zones traitées :</strong> visage (joues, mâchoire, cou, décolleté) et corps (ventre, bras, cuisses).</p>
            <p className="text-sm italic text-muted-foreground/80">C'est notre alternative premium à la chirurgie esthétique, disponible à Body by CC Sevran.</p>
          </div>
        ),
      },
      {
        question: "Qu'est-ce que la pressothérapie et pour qui est-ce recommandé ?",
        answer: (
          <div className="space-y-4">
            <p>La <strong>pressothérapie</strong> est un soin de <strong>drainage lymphatique</strong> par compression séquentielle : des bottes gonflables stimulent la circulation et l'élimination des toxines.</p>
            <p className="font-medium">✅ Idéale pour :</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2.5"><Droplets size={15} className="text-primary mt-0.5 shrink-0" /><span>Jambes lourdes & rétention d'eau</span></li>
              <li className="flex items-start gap-2.5"><Droplets size={15} className="text-primary mt-0.5 shrink-0" /><span>Cellulite aqueuse</span></li>
              <li className="flex items-start gap-2.5"><Droplets size={15} className="text-primary mt-0.5 shrink-0" /><span>Récupération post-grossesse</span></li>
              <li className="flex items-start gap-2.5"><Droplets size={15} className="text-primary mt-0.5 shrink-0" /><span>Récupération sportive</span></li>
            </ul>
            <div className="flex items-center gap-3 rounded-xl bg-accent/10 border border-accent/20 p-3">
              <Clock size={16} className="text-accent shrink-0" />
              <p className="text-sm"><strong>Durée :</strong> 45 minutes — résultats visibles dès la 1ère séance</p>
            </div>
            <p className="text-sm text-destructive/80 flex items-start gap-2"><AlertTriangle size={14} className="mt-0.5 shrink-0" />Contre-indiquée en cas de phlébite active ou d'insuffisance cardiaque sévère.</p>
          </div>
        ),
      },
      {
        question: "Qu'est-ce que le Skincleantech et en quoi est-ce différent d'un nettoyage classique ?",
        answer: (
          <div className="space-y-4">
            <p>Le <strong>Skincleantech</strong> est une technologie <strong>3-en-1</strong> qui va bien au-delà du nettoyage de peau classique :</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-border p-4 text-center">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2"><span className="font-heading font-bold text-primary text-sm">1</span></div>
                <p className="text-sm font-medium">Aspiration des impuretés</p>
              </div>
              <div className="rounded-xl border border-border p-4 text-center">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2"><span className="font-heading font-bold text-primary text-sm">2</span></div>
                <p className="text-sm font-medium">Peeling doux</p>
              </div>
              <div className="rounded-xl border border-border p-4 text-center">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2"><span className="font-heading font-bold text-primary text-sm">3</span></div>
                <p className="text-sm font-medium">Infusion de sérum personnalisé</p>
              </div>
            </div>
            <p><strong>Résultats immédiats :</strong> peau lissée, pores resserrés, teint unifié. Sans rougeurs prolongées.</p>
            <p className="text-sm italic text-muted-foreground/80">💡 Idéal en cure mensuelle ou avant un événement important.</p>
          </div>
        ),
      },
      {
        question: 'Combien de centimètres peut-on perdre avec le lipolaser ?',
        answer: (
          <div className="space-y-4">
            <p>Le <strong>lipolaser</strong> perfore les membranes des cellules graisseuses, qui libèrent leur contenu éliminé naturellement par le système lymphatique.</p>
            <div className="rounded-xl bg-accent/10 border border-accent/20 p-5 text-center">
              <p className="font-heading font-bold text-accent text-2xl">2 à 5 cm</p>
              <p className="text-sm text-muted-foreground mt-1">de perte moyenne par zone après 6 à 10 séances</p>
            </div>
            <div className="flex items-start gap-2.5 text-sm">
              <AlertTriangle size={14} className="text-accent mt-0.5 shrink-0" />
              <p><strong>Important :</strong> le lipolaser est un soin de <em>remodelage ciblé</em>, pas un traitement amaigrissant. Il est associé à la pressothérapie chez Body by CC pour des résultats optimaux.</p>
            </div>
          </div>
        ),
      },
      {
        question: 'Les soins sont-ils adaptés aux peaux noires et métissées ?',
        answer: (
          <div className="space-y-4">
            <p><strong>Oui, et c'est une spécialité de Body by CC.</strong> Carine Tchuente, infirmière diplômée et fondatrice de l'institut, maîtrise parfaitement les spécificités des <strong>peaux noires, métissées et à phototypes foncés</strong> (III à VI).</p>
            <ul className="space-y-2.5">
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span>Prévention des risques d'<strong>hyperpigmentation post-inflammatoire</strong></span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span>Peelings adaptés (acide mandélique plutôt que glycolique fort)</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span>Laser épilation Nd:YAG pour peaux foncées</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span>Protocoles anti-taches sécurisés</span></li>
            </ul>
            <p className="text-sm italic text-muted-foreground/80">Nous accueillons une large clientèle de Seine-Saint-Denis avec toutes les carnations.</p>
          </div>
        ),
      },
      {
        question: 'Proposez-vous des soins pour traiter les taches et l\'hyperpigmentation ?',
        answer: (
          <div className="space-y-4">
            <p><strong>Oui</strong>, le traitement des taches et de l'hyperpigmentation est l'une de nos expertises, particulièrement pour les <strong>peaux noires et métissées</strong>.</p>
            <p className="font-medium">Nos solutions :</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span>Peelings doux adaptés (acide mandélique, kojique)</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span>Photothérapie LED régulatrice de mélanine</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span>Protocoles anti-taches progressifs et sécurisés</span></li>
            </ul>
            <div className="flex items-center gap-3 rounded-xl bg-primary/5 border border-primary/10 p-3">
              <Clock size={16} className="text-primary shrink-0" />
              <p className="text-sm"><strong>Durée du protocole :</strong> 3 à 6 mois de suivi — SPF 50 quotidien indispensable</p>
            </div>
          </div>
        ),
      },
      {
        question: 'Proposez-vous le blanchiment dentaire ?',
        answer: (
          <div className="space-y-4">
            <p><strong>Oui</strong>, Body by CC propose le <strong>blanchiment dentaire LED</strong> à Sevran. Ce soin non invasif utilise un gel et une lampe LED pour éclaircir les dents de plusieurs teintes.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-border p-3 text-center">
                <p className="font-heading font-bold text-foreground">~1h</p>
                <p className="text-xs text-muted-foreground">Durée de la séance</p>
              </div>
              <div className="rounded-xl border border-border p-3 text-center">
                <p className="font-heading font-bold text-foreground">Immédiat</p>
                <p className="text-xs text-muted-foreground">Résultats visibles</p>
              </div>
            </div>
            <p className="text-sm text-destructive/80 flex items-start gap-2"><AlertTriangle size={14} className="mt-0.5 shrink-0" />Contre-indiqué pendant la grossesse et sur les dents sensibles non traitées.</p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'peau-noire-metissee',
    label: 'Peau noire & métissée',
    icon: <Heart size={18} />,
    emoji: '🤎',
    items: [
      {
        question: "Quel laser utilisez-vous pour l'épilation sur peau noire ?",
        answer: (
          <div className="space-y-4">
            <p>Nous utilisons le <strong>laser Nd:YAG 1064nm</strong>, le seul laser reconnu sûr et efficace pour les peaux noires, métissées et à phototypes foncés (IV à VI).</p>
            <div className="rounded-xl bg-destructive/5 border border-destructive/10 p-4">
              <p className="text-sm font-medium text-destructive/80 flex items-start gap-2"><AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>Les lasers alexandrite et diode sont <strong>déconseillés</strong> sur les peaux très foncées : risques de brûlures et de taches.</span></p>
            </div>
            <p className="text-sm italic text-muted-foreground/80">Chez Body by CC à Sevran, chaque séance laser est précédée d'un test de patch pour garantir votre sécurité.</p>
          </div>
        ),
      },
      {
        question: 'Le peeling est-il adapté aux peaux noires et métissées ?',
        answer: (
          <div className="space-y-4">
            <p><strong>Oui</strong>, à condition de choisir les bons acides.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl bg-primary/5 border border-primary/10 p-4">
                <p className="text-sm font-medium text-primary mb-1">✅ Recommandés</p>
                <p className="text-sm">Acide mandélique, acide kojique — doux et adaptés</p>
              </div>
              <div className="rounded-xl bg-destructive/5 border border-destructive/10 p-4">
                <p className="text-sm font-medium text-destructive/80 mb-1">❌ Contre-indiqués</p>
                <p className="text-sm">TCA, glycolique forte concentration — risque d'hyperpigmentation (PIH)</p>
              </div>
            </div>
            <p className="text-sm italic text-muted-foreground/80">Carine adapte systématiquement le protocole à votre carnation.</p>
          </div>
        ),
      },
      {
        question: "Comment traiter les taches et l'hyperpigmentation sur peau noire ?",
        answer: (
          <div className="space-y-4">
            <p>Les taches sur peau noire (post-acné, masque de grossesse, lentigos) se traitent par une <strong>approche progressive</strong> :</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span>Peeling doux adapté</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span>Photothérapie LED régulatrice</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span>Sérum uniformisant (niacinamide, vitamine C, acide azélaïque)</span></li>
              <li className="flex items-start gap-2.5"><Sun size={16} className="text-accent mt-0.5 shrink-0" /><span><strong>SPF 50 quotidien</strong> absolument indispensable</span></li>
            </ul>
            <div className="rounded-xl bg-destructive/5 border border-destructive/10 p-4">
              <p className="text-sm flex items-start gap-2"><AlertTriangle size={14} className="text-destructive/80 mt-0.5 shrink-0" /><span><strong>Attention :</strong> évitez les produits dépigmentants non réglementés (mercure, hydroquinone à haute dose) — ils sont dangereux.</span></p>
            </div>
          </div>
        ),
      },
      {
        question: 'La peau noire a-t-elle besoin de protection solaire ?',
        answer: (
          <div className="space-y-4">
            <p><strong>Oui, absolument.</strong> C'est l'un des mythes les plus répandus : la peau noire n'est <em>pas</em> immunisée contre les UV.</p>
            <div className="rounded-xl bg-accent/10 border border-accent/20 p-4">
              <p className="text-sm"><Sun size={14} className="inline text-accent mr-1.5" /><strong>Le SPF 50 est non-négociable</strong>, surtout après tout soin (peeling, LED, épilation laser). Les UV aggravent l'hyperpigmentation et accélèrent le vieillissement.</p>
            </div>
            <p className="text-sm italic text-muted-foreground/80">💡 Nous recommandons une crème solaire sans effet blanc (formules invisibles adaptées aux peaux foncées).</p>
          </div>
        ),
      },
      {
        question: 'Proposez-vous une routine beauté personnalisée pour peaux noires et métissées ?',
        answer: (
          <div className="space-y-4">
            <p><strong>Oui</strong>, lors de votre premier rendez-vous chez Body by CC, Carine réalise un <strong>diagnostic de peau approfondi</strong> et vous conseille une routine adaptée à :</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span>Votre carnation et phototype</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span>Vos problématiques (taches, acné, déshydratation)</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span>Votre budget</span></li>
            </ul>
            <p className="text-sm italic text-muted-foreground/80">Actifs recommandés : niacinamide, vitamine C, acide mandélique.</p>
          </div>
        ),
      },
      {
        question: 'Les soins contre la cellulite et le relâchement fonctionnent-ils sur les peaux foncées ?',
        answer: (
          <div className="space-y-3">
            <p><strong>Oui</strong>, les soins corps (HIFU, lipolaser, pressothérapie) fonctionnent <strong>indépendamment de la carnation</strong>.</p>
            <p>Ces technologies agissent sur les adipocytes, le tissu conjonctif et le système lymphatique — <em>pas sur la mélanine</em>. Aucune adaptation spécifique n'est nécessaire.</p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'epilation',
    label: 'Épilation',
    icon: <Zap size={18} />,
    emoji: '⚡',
    items: [
      {
        question: "Quelles méthodes d'épilation proposez-vous à Sevran ?",
        answer: (
          <div className="space-y-4">
            <p>Body by CC propose <strong>trois méthodes d'épilation</strong> à Sevran :</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-border p-4">
                <p className="font-heading font-bold text-foreground text-sm mb-1">🪒 Cire</p>
                <p className="text-xs text-muted-foreground">Chaude et froide, toutes zones</p>
              </div>
              <div className="rounded-xl border border-border p-4">
                <p className="font-heading font-bold text-foreground text-sm mb-1">⚡ Laser Nd:YAG</p>
                <p className="text-xs text-muted-foreground">Adapté à toutes les carnations</p>
              </div>
              <div className="rounded-xl border border-border p-4">
                <p className="font-heading font-bold text-foreground text-sm mb-1">🎯 Électrolyse</p>
                <p className="text-xs text-muted-foreground">100% définitive, poil par poil</p>
              </div>
            </div>
            <p className="text-sm italic text-muted-foreground/80">Un bilan est réalisé lors de votre première visite pour vous recommander la méthode la plus adaptée.</p>
          </div>
        ),
      },
      {
        question: "L'électrolyse est-elle vraiment définitive ?",
        answer: (
          <div className="space-y-4">
            <p><strong>Oui.</strong> L'électrolyse est la <strong>seule méthode reconnue comme définitive</strong> par la FDA américaine. Elle détruit le follicule pileux poil par poil.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl bg-primary/5 border border-primary/10 p-4">
                <p className="text-sm font-medium text-primary mb-1">Électrolyse</p>
                <p className="text-xs">✅ Définitive · ✅ Poils blancs/blonds · ✅ Zones précises</p>
              </div>
              <div className="rounded-xl bg-muted/50 border border-border p-4">
                <p className="text-sm font-medium mb-1">Laser</p>
                <p className="text-xs">Réduction permanente · Pas toujours totale · Poils foncés uniquement</p>
              </div>
            </div>
            <p className="text-sm italic text-muted-foreground/80">Le processus est plus long mais le résultat est définitif.</p>
          </div>
        ),
      },
      {
        question: 'Combien de séances laser sont nécessaires sur peau noire ?',
        answer: (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl bg-primary/5 border border-primary/10 p-4 text-center">
                <p className="font-heading font-bold text-primary text-xl">8 à 12</p>
                <p className="text-xs text-muted-foreground mt-1">séances sur peau foncée (IV-VI)</p>
              </div>
              <div className="rounded-xl bg-muted/50 border border-border p-4 text-center">
                <p className="font-heading font-bold text-foreground text-xl">6 à 8</p>
                <p className="text-xs text-muted-foreground mt-1">séances sur peau claire</p>
              </div>
            </div>
            <p>Séances espacées de <strong>6 à 8 semaines</strong> selon la zone. Résultats visibles dès la 3e séance.</p>
            <p className="text-sm italic text-muted-foreground/80">Le contraste moindre entre mélanine du poil et de la peau oblige à des paramètres plus prudents.</p>
          </div>
        ),
      },
      {
        question: "L'épilation laser est-elle adaptée aux peaux sensibles ?",
        answer: (
          <div className="space-y-3">
            <p><strong>Oui</strong>, avec le bon protocole. Nous réalisons systématiquement un <strong>test de patch</strong> avant la première séance.</p>
            <p>Une crème anesthésiante peut être appliquée si nécessaire. Les suites sont légères (rougeurs 24-48h).</p>
          </div>
        ),
      },
      {
        question: "Que faire avant et après une séance d'épilation laser ?",
        answer: (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-xl border border-border p-4">
                <p className="font-heading font-bold text-foreground text-sm mb-3">📋 Avant la séance</p>
                <ul className="space-y-1.5 text-sm">
                  <li>• Rasez la zone à J-1</li>
                  <li>• Pas de cire/pince 4 semaines avant</li>
                  <li>• Pas d'exposition solaire 4 semaines</li>
                  <li>• Pas d'autobronzant</li>
                </ul>
              </div>
              <div className="rounded-xl border border-border p-4">
                <p className="font-heading font-bold text-foreground text-sm mb-3">✨ Après la séance</p>
                <ul className="space-y-1.5 text-sm">
                  <li>• Pas de soleil direct 4 semaines</li>
                  <li>• Pas de sauna/hammam 48h</li>
                  <li>• Hydratez la zone</li>
                  <li>• SPF 50 si zone exposée</li>
                </ul>
              </div>
            </div>
            <p className="text-sm text-destructive/80 flex items-start gap-2"><AlertTriangle size={14} className="mt-0.5 shrink-0" />N'arrachez surtout pas les poils qui tombent naturellement dans les 2 semaines suivantes.</p>
          </div>
        ),
      },
      {
        question: "L'épilation laser est-elle efficace sur les poils incarnés ?",
        answer: (
          <div className="space-y-3">
            <p><strong>Oui</strong>, c'est l'un des grands avantages du laser : en détruisant le follicule pileux, il <strong>élimine définitivement le risque de poils incarnés</strong> sur les zones traitées.</p>
            <p>Particulièrement bénéfique sur le maillot et les aisselles, très sujets aux poils incarnés après la cire. L'électrolyse peut compléter le traitement pour les cas sévères.</p>
          </div>
        ),
      },
      {
        question: 'Proposez-vous des formules et forfaits épilation ?',
        answer: (
          <div className="space-y-3">
            <p><strong>Oui</strong>, Body by CC propose des <strong>formules avantageuses</strong> pour l'épilation laser (forfaits multi-séances) et pour les soins combinés.</p>
            <div className="flex items-center gap-3 rounded-xl bg-primary/5 border border-primary/10 p-3">
              <MapPin size={16} className="text-primary shrink-0" />
              <p className="text-sm">Contactez-nous au <strong>65 Bd Westinghouse, Sevran (93270)</strong> pour un devis personnalisé.</p>
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'avant-apres-soin',
    label: 'Avant & après le soin',
    icon: <Shield size={18} />,
    emoji: '🛡️',
    items: [
      {
        question: 'Y a-t-il des contre-indications aux soins visage ?',
        answer: (
          <div className="space-y-4">
            <p>Certains soins nécessitent des précautions :</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2.5 pr-4 font-heading font-bold text-foreground">Soin</th>
                    <th className="text-left py-2.5 font-heading font-bold text-foreground">Contre-indications</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-border/50"><td className="py-2.5 pr-4 font-medium text-foreground">Peelings</td><td className="py-2.5">Grossesse, Roaccutane, plaies actives</td></tr>
                  <tr className="border-b border-border/50"><td className="py-2.5 pr-4 font-medium text-foreground">LED</td><td className="py-2.5">Épilepsie photosensible, médicaments photosensibilisants</td></tr>
                  <tr><td className="py-2.5 pr-4 font-medium text-foreground">HIFU</td><td className="py-2.5">Grossesse, implants métalliques, maladies auto-immunes</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm italic text-muted-foreground/80">Un bilan préalable est toujours réalisé pour vérifier l'absence de contre-indications.</p>
          </div>
        ),
      },
      {
        question: 'Dois-je préparer ma peau avant un soin en institut ?',
        answer: (
          <div className="space-y-4">
            <ul className="space-y-2.5">
              <li className="flex items-start gap-2.5"><Sparkles size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>Soins visage :</strong> venez démaquillée (ou nous nous en chargeons)</span></li>
              <li className="flex items-start gap-2.5"><Sparkles size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>Peelings :</strong> arrêtez les rétinoïdes 7 jours avant</span></li>
              <li className="flex items-start gap-2.5"><Sparkles size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>Épilation laser :</strong> rasez J-1, pas de soleil 4 semaines avant</span></li>
              <li className="flex items-start gap-2.5"><Sparkles size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>Soins corps :</strong> hydratez bien la zone les jours précédents</span></li>
            </ul>
            <p className="text-sm italic text-muted-foreground/80">En cas de doute, contactez-nous avant votre rendez-vous — Carine vous guidera.</p>
          </div>
        ),
      },
      {
        question: 'Peut-on se maquiller après un soin visage ?',
        answer: (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2.5 pr-4 font-heading font-bold text-foreground">Soin</th>
                    <th className="text-left py-2.5 font-heading font-bold text-foreground">Maquillage après</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-border/50"><td className="py-2.5 pr-4 font-medium text-foreground">Hydratant / anti-âge</td><td className="py-2.5">✅ Immédiatement</td></tr>
                  <tr className="border-b border-border/50"><td className="py-2.5 pr-4 font-medium text-foreground">Peeling</td><td className="py-2.5">⏳ 24-48h (produits minéraux doux)</td></tr>
                  <tr className="border-b border-border/50"><td className="py-2.5 pr-4 font-medium text-foreground">LED</td><td className="py-2.5">✅ Pas de restriction</td></tr>
                  <tr><td className="py-2.5 pr-4 font-medium text-foreground">Skincleantech</td><td className="py-2.5">⏳ Quelques heures</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        ),
      },
      {
        question: 'Combien de séances faut-il pour voir des résultats ?',
        answer: (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-border p-4">
                <p className="font-heading font-bold text-foreground text-sm">Hydratation & éclat</p>
                <p className="text-xs text-muted-foreground mt-1">Dès la 1ère séance</p>
              </div>
              <div className="rounded-xl border border-border p-4">
                <p className="font-heading font-bold text-foreground text-sm">Anti-âge</p>
                <p className="text-xs text-muted-foreground mt-1">3 à 6 séances</p>
              </div>
              <div className="rounded-xl border border-border p-4">
                <p className="font-heading font-bold text-foreground text-sm">Hyperpigmentation</p>
                <p className="text-xs text-muted-foreground mt-1">3 à 6 mois de protocole</p>
              </div>
              <div className="rounded-xl border border-border p-4">
                <p className="font-heading font-bold text-foreground text-sm">Épilation laser</p>
                <p className="text-xs text-muted-foreground mt-1">Significatif dès la 3e séance</p>
              </div>
            </div>
            <p className="text-sm italic text-muted-foreground/80">Lors de votre bilan initial, Carine vous indique le nombre de séances recommandé pour votre objectif.</p>
          </div>
        ),
      },
      {
        question: 'Que faire après une séance de peeling ?',
        answer: (
          <div className="space-y-4">
            <p className="font-medium">Dans les 48 à 72h suivant un peeling :</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2.5"><Sun size={16} className="text-accent mt-0.5 shrink-0" /><span>SPF 50 <strong>obligatoire</strong> (pendant au moins 1 mois)</span></li>
              <li className="flex items-start gap-2.5"><AlertTriangle size={16} className="text-destructive/60 mt-0.5 shrink-0" /><span>Pas d'actifs agressifs (rétinoïdes, AHA, acide salicylique)</span></li>
              <li className="flex items-start gap-2.5"><AlertTriangle size={16} className="text-destructive/60 mt-0.5 shrink-0" /><span>Ne pas frotter la peau qui desquame</span></li>
              <li className="flex items-start gap-2.5"><Droplets size={16} className="text-primary mt-0.5 shrink-0" /><span>Hydratez abondamment avec une crème douce</span></li>
              <li className="flex items-start gap-2.5"><AlertTriangle size={16} className="text-destructive/60 mt-0.5 shrink-0" /><span>Pas de sauna ni hammam pendant 1 semaine</span></li>
            </ul>
          </div>
        ),
      },
      {
        question: 'Les soins font-ils mal ?',
        answer: (
          <div className="space-y-4">
            <p>La plupart de nos soins sont <strong>indolores ou très confortables</strong>. Voici le niveau de sensation par soin :</p>
            <div className="space-y-2">
              <div className="flex items-center justify-between rounded-lg bg-muted/30 px-4 py-2.5"><span className="text-sm font-medium">HIFU</span><span className="text-xs text-muted-foreground">Chaleur + légers fourmillements</span></div>
              <div className="flex items-center justify-between rounded-lg bg-muted/30 px-4 py-2.5"><span className="text-sm font-medium">Laser Nd:YAG</span><span className="text-xs text-muted-foreground">Petits coups d'élastique</span></div>
              <div className="flex items-center justify-between rounded-lg bg-muted/30 px-4 py-2.5"><span className="text-sm font-medium">Électrolyse</span><span className="text-xs text-muted-foreground">Léger picotement</span></div>
              <div className="flex items-center justify-between rounded-lg bg-muted/30 px-4 py-2.5"><span className="text-sm font-medium">Pressothérapie</span><span className="text-xs text-muted-foreground">Très confortable</span></div>
            </div>
            <p className="text-sm italic text-muted-foreground/80">Crème anesthésiante disponible si nécessaire. Carine adapte toujours les paramètres à votre confort.</p>
          </div>
        ),
      },
      {
        question: 'Faut-il éviter le soleil après les soins ?',
        answer: (
          <div className="space-y-3">
            <p><strong>Oui</strong>, pour la grande majorité des soins : peeling, épilation laser, LED, HIFU.</p>
            <div className="rounded-xl bg-accent/10 border border-accent/20 p-4">
              <p className="text-sm"><Sun size={14} className="inline text-accent mr-1.5" /><strong>SPF 50 non-négociable</strong> après tout soin actif, même par temps nuageux. Sur peau foncée, choisissez une formule invisible sans effet blanc.</p>
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'grossesse-post-partum',
    label: 'Grossesse & post-partum',
    icon: <Baby size={18} />,
    emoji: '🤰',
    items: [
      {
        question: 'Quels soins sont autorisés pendant la grossesse ?',
        answer: (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl bg-primary/5 border border-primary/10 p-4">
                <p className="text-sm font-medium text-primary mb-2">✅ Autorisés</p>
                <ul className="text-sm space-y-1">
                  <li>• Hydratation visage</li>
                  <li>• Massage doux (avec accord médical)</li>
                  <li>• Pressothérapie légère (2e trimestre+)</li>
                </ul>
              </div>
              <div className="rounded-xl bg-destructive/5 border border-destructive/10 p-4">
                <p className="text-sm font-medium text-destructive/80 mb-2">❌ Contre-indiqués</p>
                <ul className="text-sm space-y-1">
                  <li>• Peelings chimiques</li>
                  <li>• Épilation laser, HIFU, lipolaser</li>
                  <li>• Blanchiment dentaire, LED</li>
                </ul>
              </div>
            </div>
            <p className="text-sm italic text-muted-foreground/80">En cas de doute, consultez votre gynécologue avant de prendre rendez-vous.</p>
          </div>
        ),
      },
      {
        question: "Quand peut-on recommencer les soins corps après l'accouchement ?",
        answer: (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2.5 pr-4 font-heading font-bold text-foreground">Soin</th>
                    <th className="text-left py-2.5 font-heading font-bold text-foreground">Délai</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-border/50"><td className="py-2.5 pr-4 font-medium text-foreground">Soins visage doux</td><td className="py-2.5">Dès le post-partum</td></tr>
                  <tr className="border-b border-border/50"><td className="py-2.5 pr-4 font-medium text-foreground">Pressothérapie</td><td className="py-2.5">6-8 sem. (voie basse) / 12 sem. (césarienne)</td></tr>
                  <tr><td className="py-2.5 pr-4 font-medium text-foreground">HIFU, lipolaser</td><td className="py-2.5">3 mois min. + fin allaitement</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm italic text-muted-foreground/80">Carine adapte systématiquement le programme selon votre situation.</p>
          </div>
        ),
      },
      {
        question: "La pressothérapie est-elle compatible avec l'allaitement ?",
        answer: (
          <div className="space-y-3">
            <p>La pressothérapie des jambes est <strong>généralement compatible</strong> avec l'allaitement, car elle n'affecte pas les zones thoraciques.</p>
            <p className="text-sm italic text-muted-foreground/80">Par précaution, nous demandons l'accord de votre médecin ou sage-femme avant la première séance.</p>
          </div>
        ),
      },
      {
        question: 'Peut-on traiter les vergetures après la grossesse ?',
        answer: (
          <div className="space-y-4">
            <p><strong>Oui, et plus tôt c'est mieux.</strong></p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
                <p className="text-sm font-medium text-primary">Vergetures récentes</p>
                <p className="text-xs text-muted-foreground mt-1">Rouges/violacées — répondent bien aux soins (LED, régénérants)</p>
              </div>
              <div className="rounded-xl border border-border bg-muted/30 p-4">
                <p className="text-sm font-medium">Vergetures anciennes</p>
                <p className="text-xs text-muted-foreground mt-1">Blanches — améliorations possibles mais plus difficiles</p>
              </div>
            </div>
          </div>
        ),
      },
      {
        question: 'Proposez-vous un accompagnement complet post-grossesse ?',
        answer: (
          <div className="space-y-4">
            <p><strong>Oui</strong>, Body by CC propose un <strong>programme post-partum complet</strong> :</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span>Pressothérapie drainante</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span>Soins corps pour le relâchement cutané</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span>Traitement des vergetures</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span>Retrouver l'éclat du visage</span></li>
            </ul>
            <p className="text-sm italic text-muted-foreground/80">Nous accueillons de nombreuses jeunes mamans de Sevran, Aulnay-sous-Bois et du 93 avec bienveillance — sans jugement, à votre rythme.</p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'rdv-tarifs',
    label: 'Rendez-vous & tarifs',
    icon: <Calendar size={18} />,
    emoji: '📅',
    items: [
      {
        question: 'Comment prendre rendez-vous chez Body by CC à Sevran ?',
        answer: (
          <div className="space-y-4">
            <p>Plusieurs options pour prendre rendez-vous :</p>
            <ul className="space-y-2.5">
              <li className="flex items-start gap-2.5"><Phone size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>Par téléphone</strong></span></li>
              <li className="flex items-start gap-2.5"><MapPin size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>Directement à l'institut</strong> — 65 Bd Westinghouse, 93270 Sevran</span></li>
            </ul>
            <div className="flex items-center gap-3 rounded-xl bg-accent/10 border border-accent/20 p-3">
              <Clock size={16} className="text-accent shrink-0" />
              <p className="text-sm">Ouvert du <strong>mardi au dimanche, 10h-20h</strong> (fermé le lundi)</p>
            </div>
            <p className="text-sm italic text-muted-foreground/80">💡 Réservez à l'avance pour les soins techniques (HIFU, laser, lipolaser) qui nécessitent des créneaux dédiés.</p>
          </div>
        ),
      },
      {
        question: 'Peut-on annuler ou reporter un rendez-vous ?',
        answer: (
          <div className="space-y-3">
            <p><strong>Oui</strong>, tout en respectant un <strong>délai de prévenance de 24 heures minimum</strong>.</p>
            <p className="text-sm text-muted-foreground">En cas d'annulation tardive répétée, un acompte pourra être demandé pour les futures réservations de soins techniques.</p>
          </div>
        ),
      },
      {
        question: 'Proposez-vous des cartes cadeaux ?',
        answer: (
          <div className="space-y-3">
            <p><strong>Oui</strong>, Body by CC propose des <strong>cartes cadeaux</strong> pour tous les soins et montants, disponibles directement à l'institut.</p>
            <p>Idéales pour : anniversaire, Noël, fête des mères, naissance 🎁</p>
          </div>
        ),
      },
      {
        question: 'Quels modes de paiement acceptez-vous ?',
        answer: (
          <div className="space-y-3">
            <ul className="space-y-2">
              <li className="flex items-start gap-2.5"><CreditCard size={16} className="text-primary mt-0.5 shrink-0" /><span>Carte bancaire</span></li>
              <li className="flex items-start gap-2.5"><CreditCard size={16} className="text-primary mt-0.5 shrink-0" /><span>Espèces</span></li>
              <li className="flex items-start gap-2.5"><CreditCard size={16} className="text-primary mt-0.5 shrink-0" /><span>Virement</span></li>
            </ul>
            <p className="text-sm italic text-muted-foreground/80">Paiement en plusieurs fois possible pour les forfaits multi-séances — renseignez-vous à l'institut.</p>
          </div>
        ),
      },
      {
        question: 'Proposez-vous des formules et forfaits avantageuses ?',
        answer: (
          <div className="space-y-4">
            <p><strong>Oui</strong>, plusieurs formules combinées à tarif préférentiel :</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-center"><p className="text-sm font-medium">Découverte Visage</p></div>
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-center"><p className="text-sm font-medium">Peau Neuve</p></div>
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-center"><p className="text-sm font-medium">Beauté Express</p></div>
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-center"><p className="text-sm font-medium">Corps Sculpté</p></div>
            </div>
            <p className="text-sm text-muted-foreground">+ Forfaits épilation laser multi-séances disponibles. Contactez-nous pour un devis personnalisé.</p>
          </div>
        ),
      },
      {
        question: 'Le premier rendez-vous inclut-il un bilan peau ?',
        answer: (
          <div className="space-y-3">
            <p><strong>Oui.</strong> Avant tout soin technique, nous réalisons un <strong>bilan personnalisé avec Carine</strong> : analyse de peau, carnation, attentes et vérification des contre-indications.</p>
            <div className="flex items-center gap-3 rounded-xl bg-primary/5 border border-primary/10 p-3">
              <Stethoscope size={16} className="text-primary shrink-0" />
              <p className="text-sm">Ce bilan est <strong>inclus</strong> dans votre première consultation.</p>
            </div>
          </div>
        ),
      },
      {
        question: 'Proposez-vous des soins pour les hommes ?',
        answer: (
          <div className="space-y-4">
            <p><strong>Oui</strong>, plusieurs soins accueillent une clientèle masculine :</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2.5"><Users size={16} className="text-primary mt-0.5 shrink-0" /><span>Soins visage (anti-âge, acné, Skincleantech)</span></li>
              <li className="flex items-start gap-2.5"><Users size={16} className="text-primary mt-0.5 shrink-0" /><span>Épilation (torse, dos, épaules, sourcils)</span></li>
              <li className="flex items-start gap-2.5"><Users size={16} className="text-primary mt-0.5 shrink-0" /><span>HIFU corps, lipolaser</span></li>
            </ul>
          </div>
        ),
      },
    ],
  },
  {
    id: 'hygiene-securite',
    label: 'Hygiène & sécurité',
    icon: <Shield size={18} />,
    emoji: '🧼',
    items: [
      {
        question: 'Quelles sont les qualifications de Carine Tchuente ?',
        answer: (
          <div className="space-y-4">
            <p><strong>Carine Tchuente</strong> est :</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2.5"><Stethoscope size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>Infirmière diplômée d'État</strong></span></li>
              <li className="flex items-start gap-2.5"><Sparkles size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>Esthéticienne professionnelle</strong></span></li>
              <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span>Fondatrice de Body by CC</span></li>
            </ul>
            <p className="text-sm text-muted-foreground">Sa double expertise médicale et esthétique allie rigueur clinique, sécurité et sens du détail. Elle se forme régulièrement aux nouvelles technologies (HIFU, laser, LED).</p>
          </div>
        ),
      },
      {
        question: 'Le matériel est-il à usage unique ou stérilisé ?',
        answer: (
          <div className="space-y-4">
            <p>Body by CC applique les <strong>protocoles d'hygiène les plus stricts</strong> :</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2.5"><Shield size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>Usage unique :</strong> aiguilles d'électrolyse, applicateurs, spatules</span></li>
              <li className="flex items-start gap-2.5"><Shield size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>Stérilisé :</strong> matériel réutilisable désinfecté selon les normes</span></li>
              <li className="flex items-start gap-2.5"><Shield size={16} className="text-primary mt-0.5 shrink-0" /><span>Draps et serviettes changés à chaque cliente</span></li>
            </ul>
          </div>
        ),
      },
      {
        question: "Un test de patch est-il réalisé avant l'épilation laser ?",
        answer: (
          <div className="space-y-3">
            <p><strong>Oui, systématiquement.</strong> Avant toute première séance, nous réalisons un test de patch sur une petite zone pour évaluer la réaction de votre peau et ajuster les paramètres.</p>
            <p className="text-sm italic text-muted-foreground/80">Ce test est essentiel pour votre sécurité, particulièrement sur les peaux foncées.</p>
          </div>
        ),
      },
      {
        question: 'Les soins sont-ils adaptés aux personnes allergiques ?',
        answer: (
          <div className="space-y-3">
            <p>Nous vous demandons de nous informer de <strong>toutes vos allergies connues</strong> (produits cosmétiques, latex, parfums) lors de votre bilan initial.</p>
            <p>Carine adapte les produits utilisés en conséquence. En cas d'allergie sévère, un test cutané préalable peut être réalisé.</p>
            <div className="flex items-center gap-3 rounded-xl bg-primary/5 border border-primary/10 p-3">
              <Shield size={16} className="text-primary shrink-0" />
              <p className="text-sm font-medium">Votre sécurité est notre priorité absolue.</p>
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'infos-pratiques',
    label: 'Infos pratiques',
    icon: <Info size={18} />,
    emoji: '📍',
    items: [
      {
        question: 'Où se situe Body by CC à Sevran et comment y accéder ?',
        answer: (
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-xl bg-primary/5 border border-primary/10 p-4">
              <MapPin size={18} className="text-primary mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">65 Boulevard Westinghouse, 93270 Sevran</p>
                <p className="text-sm text-muted-foreground mt-1">Seine-Saint-Denis · Accessible en RER B et D, bus et voiture</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">Nous accueillons les clientes de Sevran, Aulnay-sous-Bois, Villepinte, Tremblay-en-France, Livry-Gargan, Vaujours et de toute la Seine-Saint-Denis.</p>
          </div>
        ),
      },
      {
        question: "Quels sont vos horaires d'ouverture ?",
        answer: (
          <div className="space-y-3">
            <div className="rounded-xl border border-border p-4">
              <div className="flex items-center justify-between py-1.5 border-b border-border/50 text-sm"><span className="font-medium text-destructive/70">Lundi</span><span className="font-medium text-destructive/70">Fermé</span></div>
              <div className="flex items-center justify-between py-1.5 border-b border-border/50 text-sm"><span>Mardi → Dimanche</span><span className="font-medium">10h - 20h</span></div>
            </div>
            <p className="text-sm italic text-muted-foreground/80">Des horaires larges pour accueillir les actives, les mamans et tous ceux qui souhaitent prendre soin d'eux.</p>
          </div>
        ),
      },
      {
        question: "Y a-t-il un parking à proximité de l'institut ?",
        answer: (
          <div className="space-y-3">
            <p><strong>Oui</strong>, des places de stationnement sont disponibles aux alentours du 65 Bd Westinghouse à Sevran.</p>
            <p className="text-sm text-muted-foreground">N'hésitez pas à utiliser Google Maps avec l'adresse de l'institut pour plus d'informations sur l'accès.</p>
          </div>
        ),
      },
      {
        question: 'Êtes-vous accessible aux personnes à mobilité réduite ?',
        answer: (
          <div className="space-y-3">
            <p>Contactez-nous directement pour vous informer sur l'accessibilité de l'institut. Nous faisons notre possible pour accueillir toutes nos clientes dans les meilleures conditions.</p>
          </div>
        ),
      },
      {
        question: 'Dans quelles villes intervenez-vous autour de Sevran ?',
        answer: (
          <div className="space-y-4">
            <p>Notre institut accueille des clientes de toute la <strong>Seine-Saint-Denis</strong> :</p>
            <div className="flex flex-wrap gap-2">
              {['Aulnay-sous-Bois', 'Villepinte', 'Tremblay-en-France', 'Livry-Gargan', 'Vaujours', 'Clichy-sous-Bois', 'Montfermeil', 'Gagny', 'Le Blanc-Mesnil', 'Drancy'].map((ville) => (
                <span key={ville} className="px-3 py-1.5 rounded-full bg-primary/5 border border-primary/10 text-sm">{ville}</span>
              ))}
            </div>
            <p className="text-sm italic text-muted-foreground/80">Certains services (conseil, suivi) peuvent être assurés à distance par téléphone ou visio.</p>
          </div>
        ),
      },
    ],
  },
];

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState(faqCategories[0].id);
  useCanonical('/faq');
  useEffect(() => {
    document.title = 'FAQ — Body by CC | Questions fréquentes institut beauté Sevran';
    document.querySelector('meta[name="description"]')?.setAttribute('content', 'Questions fréquentes sur les soins visage, épilation laser peau noire, HIFU, pressothérapie, grossesse et tarifs de Body by CC, institut de beauté à Sevran (93).');
  }, []);

  const activeCat = faqCategories.find((c) => c.id === activeCategory)!;

  return (
    <>
      {/* Hero */}
      <section className="px-6 lg:px-8 py-20 md:py-28 bg-petrol text-petrol-foreground">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-heading font-medium uppercase tracking-[0.2em] text-accent mb-4">Aide & informations</p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold">Questions fréquentes</h1>
          <p className="mt-5 text-petrol-foreground/60 max-w-xl text-base md:text-lg leading-relaxed font-body">
            Retrouvez ici les réponses aux questions les plus fréquentes sur nos soins, nos tarifs et notre institut.
          </p>
        </div>
      </section>

      {/* FAQ content */}
      <section className="px-6 lg:px-8 py-16 md:py-24 bg-background">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-10 lg:gap-16">
          {/* Sidebar nav */}
          <nav className="lg:w-72 shrink-0">
            <ul className="flex flex-col gap-1.5 lg:sticky lg:top-32">
              {faqCategories.map((cat) => {
                const isActive = activeCategory === cat.id;
                return (
                  <li key={cat.id}>
                    <button
                      onClick={() => setActiveCategory(cat.id)}
                      className={`text-left w-full px-5 py-4 text-sm font-semibold uppercase tracking-wider transition-all border-l-4 rounded-r-xl font-body flex items-center gap-3 ${
                        isActive
                          ? 'border-l-primary text-primary bg-primary/5'
                          : 'border-l-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30'
                      }`}
                    >
                      {cat.label}
                      {isActive && <ChevronRight size={14} className="ml-auto text-primary" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Questions */}
          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCat.id}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center gap-3 mb-8">
                  <span className="text-2xl">{activeCat.emoji}</span>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">{activeCat.label}</h2>
                </div>
                <Accordion type="single" collapsible className="space-y-4">
                  {activeCat.items.map((item, idx) => (
                    <AccordionItem
                      key={idx}
                      value={`${activeCat.id}-${idx}`}
                      className="border border-border rounded-2xl px-6 md:px-8 bg-card data-[state=open]:border-primary/30 data-[state=open]:shadow-sm transition-all"
                    >
                      <AccordionTrigger className="font-heading text-base md:text-lg font-semibold text-foreground hover:no-underline py-5">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-6 font-body">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      <CTASection
        title="Vous n'avez pas trouvé votre réponse ?"
        subtitle="Contactez-nous directement, nous serons ravies de vous renseigner."
        buttonLabel="Nous contacter"
        buttonLink="/contact"
      />
    </>
  );
};

export default FAQ;
